/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Flex, Stack, Text, Button, useBreakpointValue, useMultiStyleConfig} from '@chakra-ui/react'
import {useProducts, useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import UnavailableProductConfirmationModal from '@salesforce/retail-react-app/app/components/unavailable-product-confirmation-modal'

// import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
// import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
// import {useGetCategoriesData} from '../../custom-api/hooks/use-get-categories-data'

import WishlistProductItem from './wishlist/partials/wishlist-product-item'
import WishlistPrimaryAction from './wishlist/partials/wishlist-primary-action'

const WishlistWidget = ({wishListData, isWishListLoading}) => {
    const navigate = useNavigation()
    // const {addLayer} = useGoogleTagManager()
    // const categories = useGetCategoriesData()
    const styles = useMultiStyleConfig('Wishlist')

    const intl = useIntl()

    const headingRef = useRef()
    useEffect(() => {
        // Focus the 'Wishlist' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    const productIds = wishListData?.customerProductListItems?.map((item) => item.productId)

    const {data: productsData, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds?.join(','),
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'account-index',
            },
        },
        {enabled: productIds?.length > 0},
    )

    // If a product is added to the wishlist many times, it gets collapased into 1 line item
    const wishListItems = wishListData?.customerProductListItems?.reduce((itemsData, item) => {
        const productData = productsData?.data?.find((product) => product.id === item.productId)
        return {...itemsData, [item.productId]: {...item, product: productData}}
    }, {})

    const deleteCustomerProductListItem = useShopperCustomersMutation(
        'deleteCustomerProductListItem',
    )

    const {data: customer, isFetching: isCustomerFetching} = useCurrentCustomer()

    const handleUnavailableProducts = async (unavailableProductIds) => {
        if (!unavailableProductIds.length) return
        await Promise.all(
            unavailableProductIds.map(async (id) => {
                const item = wishListItems?.find((item) => {
                    return item.productId.toString() === id.toString()
                })
                const parameters = {
                    customerId: customer.customerId,
                    itemId: item?.id,
                    listId: wishListData?.id,
                }
                await deleteCustomerProductListItem.mutateAsync({parameters})
            }),
        )
    }

    const hasWishlistItems = Object.keys(wishListItems ?? {}).length > 0
    const isPageLoading =
        isCustomerFetching || hasWishlistItems ? isProductsLoading : isWishListLoading

    // Determina il numero massimo di elementi da mostrare in base al breakpoint
    const maxItemsToShow = useBreakpointValue({base: 2, lg: 3})

    // Ottieni gli ultimi elementi della wishlist
    const limitedWishlistItems = Object.keys(wishListItems ?? {})
        .slice(-maxItemsToShow) // Prendi gli ultimi `maxItemsToShow` elementi
        .map((key) => wishListItems[key])
    // useEffect(() => {
    //     if (hasWishlistItems && !isProductsLoading) {
    //         addLayer(polish_ecommerce_gtm_event.bind(this))
    //         addLayer(
    //             item_gtm_event.bind(this, {
    //                 event: 'view_item_list',
    //                 item_list_name: 'wishlist',
    //                 items: Object.keys(wishListItems).map((key, i) => {
    //                     const item = wishListItems[key]
    //                     return {
    //                         item_name: item?.product?.name,
    //                         item_id: item?.productId,
    //                         item_category:
    //                             categories[item?.product?.primaryCategoryId]?.displayName,
    //                         item_variant: item?.productId,
    //                         price: item?.product?.price,
    //                         fullprice: getPriceData(item?.product)?.isOnSale
    //                             ? 'reduced'
    //                             : 'fullprice',
    //                         rating_points: '', //?Value undecided or missing
    //                         erp_category: '', //?Value undecided or missing
    //                         prdha_category: '', //?Value undecided or missing
    //                         index: i + 1,
    //                         stock_status:
    //                             item?.product?.inventory?.stockLevel > 0
    //                                 ? 'in-stock'
    //                                 : 'out-of-stock',
    //                     }
    //                 }),
    //             }),
    //         )
    //     }
    // }, [hasWishlistItems, isProductsLoading])

    return (
        <Stack {...styles.wishlistWidgetStack}>
            <Stack {...styles.wishlistLogin}>
                <Flex {...styles.wishlistWidget}>
                    <Text {...styles.wishlistWidgetTitle} ref={headingRef}>
                        <FormattedMessage
                            defaultMessage="Wishlist"
                            id="account_wishlist.title.wishlist"
                        />
                    </Text>
                    <Button
                        onClick={() => navigate('/account/wishlist')}
                        variant="text-button"
                        aria-label={intl.formatMessage({
                            id: 'account_wishlist.button.see_your_wishlist',
                            defaultMessage: 'see your wishlist',
                        })}
                        fontWeight="normal"
                        _focusVisible={{
                            boxShadow: 'var(--chakra-shadows-outline)',
                        }}
                    >
                        <Text>
                            <FormattedMessage
                                defaultMessage="see your wishlist"
                                id="account_wishlist.button.see_your_wishlist"
                            />
                        </Text>
                    </Button>
                </Flex>
            </Stack>

            <Flex {...styles.gridProductWidget}>
                {!isPageLoading &&
                    wishListItems &&
                    limitedWishlistItems.map((item) => (
                        <WishlistProductItem
                            key={item.id}
                            product={{
                                ...item.product,
                                quantity: item.quantity,
                            }}
                            primaryAction={<WishlistPrimaryAction />}
                        />
                    ))}
            </Flex>
            <UnavailableProductConfirmationModal
                productItems={wishListData?.customerProductListItems}
                handleUnavailableProducts={handleUnavailableProducts}
            />
        </Stack>
    )
}
WishlistWidget.propTypes = {
    wishListData: PropTypes.object,
    isWishListLoading: PropTypes.bool,
}

export default WishlistWidget
